<template>
  <div id="cesium-container" ref="cesiumContainer">
    <div id="display-options-checkboxes" ref="displayOptionsCheckboxes">
      <v-checkbox
        :label="'Payloads'"
        v-model="showPayloads"
        :disabled="!showInitialConstellation && !showOptimizedConstellation"
        :dark="true"
        color="info"
      />
      <v-checkbox
        v-if="merged"
        v-model="showInitialConstellation"
        :dark="true"
        color="info"
      >
        <template v-slot:label>
          <span style="border-bottom: 2px solid rgba(255, 0, 0, 0.6)">
            Initial Constellation
          </span>
        </template>
      </v-checkbox>
      <v-checkbox
        v-if="merged"
        v-model="showOptimizedConstellation"
        :dark="true"
        color="info"
      >
        <template v-slot:label>
          <span style="border-bottom: 2px dashed red">
            Optimized Constellation
          </span>
        </template>
      </v-checkbox>
      <v-checkbox
        :label="'Zones of Interest'"
        v-model="showZOI"
        :dark="true"
        color="info"
      />
    </div>
    <cesium-recorder ref="cesiumRecorder" />
  </div>
</template>

<script>
import __ from 'lodash'
import { CesiumInitialization } from '../../components/ThreeD/CesiumInitialization'
import { PointOfInterest } from '../../components/ThreeD/PointOfInterest'
import { ZoneOfInterest } from '../../components/ThreeD/ZoneOfInterest'
import { EarthMesh } from '../../components/ThreeD/EarthMesh'
import { AnalyticalOrbit } from '../../components/ThreeD/AnalyticalOrbit'
import CesiumRecorder from '../../components/ThreeD/CesiumRecorder.vue'
import constellationUtils from '../../mixins/constellationUtils'
import { mapGetters } from 'vuex'
import * as Cesium from 'cesium'
import CesiumTools from '../../components/ThreeD/CesiumTools'
import CesiumConstellationsTools from '../../components/ThreeD/CesiumConstellationsTools'

export default {
  name: 'groundTracks-cesium-viewer',
  mixins: [
    CesiumInitialization,
    constellationUtils,
    AnalyticalOrbit,
    PointOfInterest,
    EarthMesh,
    ZoneOfInterest
  ],
  props: [
    'pointOfInterest',
    'meshes',
    'meshesPoints',
    'tracesResults',
    'name',
    'initialGuessResults',
    'merged'
  ],
  components: {
    'cesium-recorder': CesiumRecorder
  },
  data() {
    return {
      index: 0,
      initialTracesPC: null,
      optimalTracesPC: null,
      pointsOfInterestPC: null,
      meshesPC: null,
      showZOI: true,
      billboardCollection: null,
      labelCollection: null,
      payloadsDS: null,
      showPayloads: false,
      showInitialConstellation: true,
      showOptimizedConstellation: true,
      colorsArray: [],
      positionsTimeStep: 0
    }
  },
  computed: {
    ...mapGetters({
      currentResultsLog: 'currentResultsLog',
      currentMission: 'currentMission'
    }),
    maxOrbitalPeriod() {
      const results = this.currentResultsLog.results
      let planeSMA = 0
      if (this.name === 'optimal') {
        if (
          results.initialConstellationResult.computedConstellation
            .computedHomogeneousConstellationGeometries.length > 1
        ) {
          // find maximum semi major axis
          planeSMA =
            results.initialConstellationResult.computedConstellation.computedHomogeneousConstellationGeometries.reduce(
              (total, current) =>
                total > current.plane.semiMajorAxis
                  ? total
                  : current.plane.semiMajorAxis,
              0
            )
        } else {
          planeSMA =
            results.initialConstellationResult.computedConstellation
              .computedHomogeneousConstellationGeometries[0].plane.semiMajorAxis
        }
      } else {
        if (
          results.constellationOptimizationResult.computedConstellation
            .computedHomogeneousConstellationGeometries.length > 1
        ) {
          // find maximum semi major axis
          planeSMA =
            results.constellationOptimizationResult.computedConstellation.computedHomogeneousConstellationGeometries.reduce(
              (total, current) =>
                total > current.plane.semiMajorAxis
                  ? total
                  : current.plane.semiMajorAxis,
              0
            )
        } else {
          planeSMA =
            results.initialConstellationResult.computedConstellation
              .computedHomogeneousConstellationGeometries[0].plane.semiMajorAxis
        }
      }
      return 2 * Math.PI * Math.sqrt(planeSMA ** 3 / 3.986004415e14)
    },
    nbOfSatsInInitialConstellations() {
      return this.getNumberOfItemsOfProperty(
        'initialConstellationResult',
        'numberOfSpacecrafts'
      )
    },
    nbOfSatsInOptimizedConstellations() {
      return this.getNumberOfItemsOfProperty(
        'constellationOptimizationResult',
        'numberOfSpacecrafts'
      )
    },
    nbOfPlanesInInitialConstellations() {
      return this.getNumberOfItemsOfProperty(
        'initialConstellationResult',
        'numberOfPlanes'
      )
    },
    nbOfPlanesInOptimizedConstellations() {
      return this.getNumberOfItemsOfProperty(
        'constellationOptimizationResult',
        'numberOfPlanes'
      )
    },

    totalNumberOfSats() {
      return (
        this.nbOfSatsInInitialConstellations +
        this.nbOfSatsInOptimizedConstellations
      )
    },
    alongTrackDistance() {
      return this.currentResultsLog.results.payloads[0].fieldOfView
        .groundRangeAlongTrackOperatingAltitude
    },
    crossTrackDistance() {
      return this.currentResultsLog.results.payloads[0].fieldOfView
        .groundRangeCrossTrackOperatingAltitude
    }
  },
  watch: {
    tracesResults: {
      handler() {
        this.addTracesAndPayloads()
      },
      deep: true
    },
    pointOfInterest: {
      handler() {
        this.addPointsOfInterest(this.pointOfInterest)
      },
      deep: true
    },
    meshesPoints() {
      this.onMeshesListChange(this.meshes, this.meshesPoints)
    },
    showPayloads(show) {
      for (let i = 0; i < this.index; i++) {
        this.payloadsDS.entities.getById(i).ellipse.show = show
      }
    },
    showInitialConstellation(show) {
      for (let i = 0; i < this.nbOfPlanesInInitialConstellations; i++) {
        this.initialTracesPC.get(i).show = show
      }

      if (this.merged) {
        for (let i = 0; i < this.nbOfSatsInInitialConstellations; i++) {
          this.payloadsDS.entities.getById(i).show = show
        }
      }
      this.showInitialPayloads = show ? this.showInitialPayloads : false
    },
    showOptimizedConstellation(show) {
      for (let i = 0; i < this.nbOfPlanesInOptimizedConstellations; i++) {
        this.optimalTracesPC.get(i).show = show
      }

      if (this.merged) {
        for (
          let i = this.nbOfSatsInInitialConstellations;
          i < this.totalNumberOfSats;
          i++
        ) {
          this.payloadsDS.entities.getById(i).show = show
        }
      }
      this.showOptimizedPayloads = show ? this.showOptimizedPayloads : false
    },
    showZOI(show) {
      this.meshesPC.show = show
      this.pointsOfInterestPC.show = show
    }
  },

  async mounted() {
    this.initializeViewer(false, true, true)
    this.insertElementInToolbar(this.$refs.cesiumRecorder.$el)
    this.insertElementInToolbar(this.$refs.displayOptionsCheckboxes)
    this.deactivateControls()
    this.hideSceneModePicker()
    this.cameraShouldFly = false

    const defaultRectangle = Cesium.Rectangle.fromDegrees(-180, -90, 180, 90)
    this.viewer.scene.mode = Cesium.SceneMode.SCENE2D
    Cesium.Camera.DEFAULT_VIEW_RECTANGLE = defaultRectangle
    this.initializePrimitives()
    this.getDatesAndSetClock()
    if (!this.merged) {
      this.$root.$on('clockUpdate', this.clockUpdateHandler)
    }
    this.addTracesAndPayloads()
    this.addPointsOfInterest(this.pointOfInterest)
    this.onMeshesListChange(this.meshes, this.meshesPoints)
  },

  beforeDestroy() {
    this.$root.$off('clockUpdate', this.clockUpdateHandler)
  },

  methods: {
    clockUpdateHandler(currentTime) {
      this.viewer.clock.currentTime = currentTime
    },

    getDatesAndSetClock() {
      const [startDate, endDate] = this.getSimulationDates()
      this.setCesiumClock(startDate, endDate)
      if (!this.merged) {
        this.viewer.clock.onTick.addEventListener((clock) =>
          this.syncClocks(clock)
        )
      }
      this.lastUpdated = startDate
    },

    getSimulationDates() {
      const results = this.currentResultsLog.results
      const startDate = Cesium.JulianDate.fromIso8601(results.simulationDate)
      let initialEndDate = new Cesium.JulianDate()
      let optimizedEndDate = new Cesium.JulianDate()
      Cesium.JulianDate.addSeconds(
        startDate,
        this.maxOrbitalPeriod,
        initialEndDate
      )
      Cesium.JulianDate.addSeconds(
        startDate,
        this.maxOrbitalPeriod,
        optimizedEndDate
      )
      let stopDate = new Cesium.JulianDate()
      if (this.merged) {
        stopDate = Cesium.JulianDate.greaterThan(
          initialEndDate,
          optimizedEndDate
        )
          ? initialEndDate
          : optimizedEndDate
      } else if (this.name === 'optimal') {
        stopDate = optimizedEndDate
      } else {
        stopDate = initialEndDate
      }
      return [startDate, stopDate]
    },

    syncClocks(clock) {
      if (
        Math.abs(
          Cesium.JulianDate.secondsDifference(
            clock.currentTime,
            this.lastUpdated
          )
        ) > 1
      ) {
        this.$root.$emit('clockUpdate', clock.currentTime)
        this.lastUpdated = clock.currentTime
      }
    },

    initializePrimitives() {
      this.optimalTracesPC = new Cesium.PolylineCollection()
      this.initialTracesPC = new Cesium.PolylineCollection()

      this.payloadsDS = new Cesium.CustomDataSource('payloadsDS')
      this.viewer.dataSources.add(this.payloadsDS)

      this.viewer.scene.primitives.add(this.optimalTracesPC)
      this.viewer.scene.primitives.add(this.initialTracesPC)

      this.meshesPC = this.initMeshesPC()
      this.pointsOfInterestPC = this.initPointsOfInterestPC()

      this.viewer.scene.primitives.lowerToBottom(this.meshesPC)
      this.viewer.scene.primitives.raiseToTop(this.optimalTracesPC)
      this.viewer.scene.primitives.raiseToTop(this.pointsOfInterestPC)
    },

    addTracesAndPayloads() {
      let traces = null
      let primitiveCollection = null
      let dashed = false
      if (!this.merged) {
        if (this.name === 'initial') {
          traces = this.tracesResults.initial.traces
          primitiveCollection = this.initialTracesPC
          dashed = false
        } else {
          traces = this.tracesResults.optimal.traces
          primitiveCollection = this.optimalTracesPC
          dashed = true
        }
        this.createColorsArray()
        this.addAnalyticalTraces(traces, {
          primitiveCollection: primitiveCollection,
          dashed: dashed,
          colorsArray: this.colorsArray
        })
        this.addPayloads(traces, this.payloadsDS, 1, dashed)
      } else {
        if (
          !this.tracesResults.initial.traces ||
          !this.tracesResults.optimal.traces
        ) {
          return
        }
        this.createColorsArray()
        traces = this.tracesResults.initial.traces
        this.addAnalyticalTraces(traces, {
          primitiveCollection: this.initialTracesPC,
          dashed: false,
          colorsArray: this.colorsArray,
          colorAlpha: 0.2
        })
        this.addPayloads(traces, this.payloadsDS, 0.2)
        traces = this.tracesResults.optimal.traces
        this.addAnalyticalTraces(traces, {
          primitiveCollection: this.optimalTracesPC,
          dashed: true,
          colorsArray: this.colorsArray,
          colorAlpha: 1
        })
        this.addPayloads(traces, this.payloadsDS, 1, true)
      }
    },

    createColorsArray() {
      this.colorsArray = []
      const randomHSLValue = (h) => [
        h,
        (Math.floor(Math.random() * (80 - 60 + 1)) + 80) / 100,
        (Math.floor(Math.random() * (50 - 40 + 1)) + 40) / 100
      ]
      const getNbOfColors = () => {
        if (this.merged) {
          return Math.max(
            this.nbOfPlanesInInitialConstellations,
            this.nbOfPlanesInOptimizedConstellations
          )
        } else {
          return this.name === 'initial'
            ? this.nbOfPlanesInInitialConstellations
            : this.nbOfPlanesInOptimizedConstellations
        }
      }
      const nbOfColors = getNbOfColors()
      let hValues = []
      for (let i = 0; i < nbOfColors; i++) {
        hValues.push(i / nbOfColors)
      }
      hValues.forEach((hue) => {
        const rgba = CesiumTools.hslToRgb(randomHSLValue(hue)).map(
          (value) => value / 255
        )
        this.colorsArray.push(
          new Cesium.Color(rgba[0], rgba[1], rgba[2], rgba[3])
        )
      })
    },

    addPayloads(traces, dataSource, colorAlpha, dashed = false) {
      if (!traces || !traces.length) {
        return
      }
      traces.forEach((constellation, constellationIndex) => {
        const startDate = Cesium.JulianDate.fromIso8601(
          this.currentResultsLog.results.simulationDate
        )
        const {
          numberOfSpacecraftsPerPlane,
          intraPlanePhasing,
          interPlanePhasing,
          semiMajorAxis
        } = this.getConstellationsPlaneParameters(
          !dashed
            ? 'initialConstellationResult'
            : 'constellationOptimizationResult',
          constellationIndex
        )
        constellation.forEach((tracePositions, planeIndex) => {
          const length = tracePositions.length
          const orbitalPeriod =
            CesiumConstellationsTools.getOrbitalPeriodFromSMA(semiMajorAxis)
          const timeStep = orbitalPeriod / length
          const color = Cesium.Color.fromAlpha(
            this.colorsArray[
              constellationIndex * constellation.length + planeIndex
            ],
            colorAlpha
          )
          for (
            let satIndex = 0;
            satIndex < numberOfSpacecraftsPerPlane;
            satIndex++
          ) {
            const totalShift = CesiumConstellationsTools.computeShift(
              length,
              planeIndex,
              satIndex,
              intraPlanePhasing,
              interPlanePhasing
            )
            const satPositions =
              CesiumConstellationsTools.createShiftedPosArray(
                tracePositions,
                totalShift,
                timeStep
              )
            const sampledPositionProperty = this.createSampledPositionProperty(
              satPositions,
              startDate,
              timeStep
            )
            this.addPayloadEntity(
              dataSource,
              sampledPositionProperty,
              color,
              dashed
            )
          }
        })
      })
    },

    addPayloadEntity(dataSource, sampledPositionProperty, color, dashed) {
      const results = this.currentResultsLog.results
      if (
        results.payloads[0].fieldOfView.fieldOfViewType.includes('CIRCULAR')
      ) {
        this.addCircleEntity(dataSource, {
          index: this.index,
          positions: sampledPositionProperty,
          color: color,
          dashed: dashed
        })
      } else {
        this.addRectangularPolylineEntity(dataSource, {
          index: this.index,
          positions: sampledPositionProperty,
          color: color,
          dashed: dashed
        })
      }
      this.index++
    },

    addRectangularPolylineEntity(dataSource, payloadParams) {
      const color = payloadParams.color
      const material = payloadParams.dashed
        ? new Cesium.PolylineDashMaterialProperty({
            color: color,
            dashLength: 20,
            dashPattern: 255
          })
        : new Cesium.ColorMaterialProperty(color)
      dataSource.entities.add(
        new Cesium.Entity({
          id: payloadParams.index,
          position: payloadParams.positions,
          polyline: new Cesium.PolylineGraphics({
            clampToGround: false,
            material: material,
            positions: new Cesium.CallbackProperty(
              () =>
                this.getRectangularPolylineCoordinates(payloadParams.positions),
              false
            ),
            width: 2,
            show: false
          }),
          point: {
            pixelSize: 8,
            color: color,
            outlineWidth: 2,
            outlineColor: Cesium.Color.BLACK
          }
        })
      )
    },

    getRectangularPolylineCoordinates(positions) {
      const retrieveSatPositions = (sampledPositionProperty) => {
        const currentTime = this.viewer.clock.currentTime
        const timePlusOne = new Cesium.JulianDate()
        Cesium.JulianDate.addSeconds(currentTime, 1, timePlusOne)
        const currSatPosFixed = sampledPositionProperty.getValue(currentTime)

        const nextSatPosFixed = sampledPositionProperty.getValue(timePlusOne)

        if (!currSatPosFixed || !nextSatPosFixed) {
          return [null, null]
        }

        return [currSatPosFixed, nextSatPosFixed]
      }

      const createDirectionVectors = () => {
        const alongTrackDirection = new Cesium.Cartesian3()
        const crossTrackDirection = new Cesium.Cartesian3()
        const downDirection = new Cesium.Cartesian3()

        Cesium.Cartesian3.subtract(
          nextSatPosFixed,
          currSatPosFixed,
          alongTrackDirection
        )
        // if really needed check ConstellationViewer.vue to re-implement it here
        // checkConstantALTDirection(id, alongTrackDirection)

        Cesium.Cartesian3.negate(currSatPosFixed, downDirection)

        Cesium.Cartesian3.cross(
          alongTrackDirection,
          currSatPosFixed,
          crossTrackDirection
        )
        return [alongTrackDirection, downDirection, crossTrackDirection]
      }

      const getNSD = () => {
        const alongTrackRay = new Cesium.Ray(
          currSatPosFixed,
          alongTrackDirection
        )

        const North = new Cesium.Cartesian3()
        const South = new Cesium.Cartesian3()
        const Down = new Cesium.Cartesian3()

        Cesium.Ray.getPoint(alongTrackRay, this.alongTrackDistance, North)
        Cesium.Ray.getPoint(alongTrackRay, -this.alongTrackDistance, South)
        return [North, South, Down]
      }

      const computeVertexPos = (ns, we) => {
        let posFixed = new Cesium.Cartesian3()
        let intPoint = new Cesium.Cartesian3()
        let crossTrackRay = null
        if (ns === 'North') {
          crossTrackRay = new Cesium.Ray(North, crossTrackDirection)
        } else {
          crossTrackRay = new Cesium.Ray(South, crossTrackDirection)
        }
        if (we === 'East') {
          Cesium.Ray.getPoint(crossTrackRay, this.crossTrackDistance, intPoint)
        } else {
          Cesium.Ray.getPoint(crossTrackRay, -this.crossTrackDistance, intPoint)
        }
        const downRay = new Cesium.Ray(intPoint, downDirection)
        const downDistance = Cesium.IntersectionTests.rayEllipsoid(
          downRay,
          this.viewer.scene.globe.ellipsoid
        ).start
        Cesium.Ray.getPoint(downRay, downDistance, posFixed)
        return posFixed
      }

      const [currSatPosFixed, nextSatPosFixed] = retrieveSatPositions(positions)

      // avoid crashing before the end of simulation
      if (!currSatPosFixed || !nextSatPosFixed) {
        return
      }

      const [alongTrackDirection, downDirection, crossTrackDirection] =
        createDirectionVectors()

      const [North, South, Down] = getNSD()

      const NW = computeVertexPos('North', 'West')
      const NE = computeVertexPos('North', 'East')
      const SE = computeVertexPos('South', 'East')
      const SW = computeVertexPos('South', 'West')
      const finalPoints = [SW, SE, NE, NW, SW]

      return finalPoints
    },

    createSampledPositionProperty(positionsArray, startDate) {
      const sampledPositionProperty = new Cesium.SampledPositionProperty()
      sampledPositionProperty.setInterpolationOptions(
        Cesium.LagrangePolynomialApproximation,
        3
      )
      sampledPositionProperty.addSamplesPackedArray(positionsArray, startDate)
      return sampledPositionProperty
    },

    addCircleEntity(dataSource, payloadParams) {
      const color = payloadParams.color
      dataSource.entities.add(
        new Cesium.Entity({
          id: payloadParams.index,
          position: payloadParams.positions,
          ellipse: {
            semiMinorAxis: this.crossTrackDistance,
            semiMajorAxis: this.crossTrackDistance,
            height: 0,
            outline: true,
            outlineColor: color,
            fill: false,
            show: false
          },
          point: {
            pixelSize: 8,
            color: color,
            outlineWidth: 2,
            outlineColor: Cesium.Color.BLACK
          }
        })
      )
    },

    getCircularPolylineCoordinates(positions) {
      const center = positions.getValue(this.viewer.clock.currentTime)
      if (!center) {
        return
      }
      let p1 = new Cesium.Cartesian3()
      let direction = new Cesium.Cartesian3()
      let rotationQuat = new Cesium.Quaternion()
      Cesium.Cartesian3.mostOrthogonalAxis(center, direction)
      let ray = new Cesium.Ray(center, direction)
      Cesium.Ray.getPoint(ray, this.crossTrackDistance, p1)
      let pos = []
      for (let theta = 0; theta < 361; theta += 10) {
        Cesium.Quaternion.fromAxisAngle(
          center,
          Cesium.Math.toRadians(theta),
          rotationQuat
        )
        var m = Cesium.Matrix3.fromQuaternion(rotationQuat)
        let point = Cesium.Matrix3.multiplyByVector(
          m,
          p1,
          new Cesium.Cartesian3()
        )
        let centerCarto = Cesium.Cartographic.fromCartesian(point)
        centerCarto.height = 0
        pos.push(
          Cesium.Cartographic.toCartesian(
            centerCarto,
            this.viewer.scene.globe.ellipsoid
          )
        )
      }
      return pos
    },

    getConstellationsPlaneParameters(constellationName, geometryIndex) {
      let params = {
        numberOfSpacecraftsPerPlane: 0,
        intraPlanePhasing: 0,
        interPlanePhasing: 0,
        semiMajorAxis: 0
      }
      Object.keys(params).forEach((key) => {
        params[key] = this.getValueFromConstellationGeometryProperty(
          constellationName,
          geometryIndex,
          key
        )
      })
      params.semiMajorAxis =
        this.currentResultsLog.results[
          constellationName
        ].computedConstellation.computedHomogeneousConstellationGeometries[
          geometryIndex
        ].plane.semiMajorAxis
      return params
    },

    getNumberOfItemsOfProperty(constellationName, propertyName) {
      if (
        this.currentResultsLog.results[constellationName].computedConstellation
          .computedHomogeneousConstellationGeometries.length > 1
      ) {
        let initial = 0
        // this.currentResultsLog.results[constellationName]
        //   .computedConstellation
        //   .computedHomogeneousConstellationGeometries[0][propertyName]
        return this.currentResultsLog.results[
          constellationName
        ].computedConstellation.computedHomogeneousConstellationGeometries.reduce(
          (total, current) => total + current[propertyName],
          initial
        )
      } else {
        return this.currentResultsLog.results[constellationName]
          .computedConstellation.computedHomogeneousConstellationGeometries[0][
          propertyName
        ]
      }
    },

    getValueFromConstellationGeometryProperty(
      constellationName,
      geometryIndex,
      property
    ) {
      const results = this.currentResultsLog.results
      return results[constellationName].computedConstellation
        .computedHomogeneousConstellationGeometries[geometryIndex][property]
    }
  }
}
</script>

<style scoped>
#cesium-container {
  z-index: 2;
}

#display-options-checkboxes {
  z-index: 3;
  vertical-align: middle;
  display: inline-grid;
  grid-auto-flow: column;
}

#display-options-checkboxes .v-input--selection-controls.v-input {
  background-color: #303336;
  border: 1px solid #444;
  position: relative;
  display: inline-block;
  height: 32px;
  line-height: 32px;
  margin: 2px 3px;
  padding: 2px;
}
</style>
